import { GoToRowLink } from "@/components/verification/GoToRowLink";
import { type FileUploadStatus, type ParsingResult, useUploadStore } from "@/stores/upload-store";
import {
	AlertTriangle,
	BookOpen,
	CheckCircle,
	ChevronDown,
	ChevronUp,
	Clock,
	Loader2,
	SkipForward,
	X,
	XCircle,
} from "lucide-react";
import { useState } from "react";

const statusConfig: Record<
	FileUploadStatus,
	{ icon: React.ReactNode; label: string; color: string }
> = {
	queued: {
		icon: <Clock className="h-4 w-4" />,
		label: "В очереди",
		color: "text-slate-400",
	},
	processing: {
		icon: <Loader2 className="h-4 w-4 animate-spin" />,
		label: "Обработка",
		color: "text-blue-500",
	},
	done: {
		icon: <CheckCircle className="h-4 w-4" />,
		label: "Готово",
		color: "text-green-500",
	},
	error: {
		icon: <XCircle className="h-4 w-4" />,
		label: "Ошибка",
		color: "text-red-500",
	},
	skipped: {
		icon: <SkipForward className="h-4 w-4" />,
		label: "Пропущен",
		color: "text-red-500",
	},
};

const parsingResultLabels: Record<ParsingResult["fileType"], string> = {
	rate_book: "Книга расценок",
	work_order: "Наряд",
	completion_act: "Акт",
};

function ParsingResultCard({ result }: { result: ParsingResult }) {
	const [expanded, setExpanded] = useState(false);

	// v3 completion-act validation — рендерится отдельно (US2 T031).
	if (result.fileType === "completion_act" && result.completionActValidation) {
		return <CompletionActValidationCard validation={result.completionActValidation} />;
	}

	const hasErrors = result.errors.length > 0;

	return (
		<div className="mt-2 rounded-md border border-blue-100 bg-blue-50 p-3">
			<div className="flex items-center gap-2 text-sm">
				<BookOpen className="h-4 w-4 text-blue-600" />
				<span className="font-medium text-blue-900">{parsingResultLabels[result.fileType]}</span>
				<span className="text-blue-700">
					Загружено: {result.parsedCount}, пропущено: {result.skippedCount}
				</span>
			</div>

			{hasErrors && (
				<div className="mt-2">
					<button
						type="button"
						onClick={() => setExpanded(!expanded)}
						className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900"
					>
						{expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
						{expanded ? "Скрыть ошибки" : `Показать ошибки (${result.errors.length})`}
					</button>

					{expanded && (
						<ul className="mt-1 space-y-0.5 text-xs text-red-700">
							{result.errors.map((err) => (
								<li key={err.row}>{err.reason}</li>
							))}
						</ul>
					)}
				</div>
			)}
		</div>
	);
}

/**
 * Карточка результата парсинга акта v3 (US2 T031).
 *
 * Показывает:
 * - 3 флага валидации (arithmeticOk / subtotalsOk / grandTotalOk) с цветными иконками;
 * - счётчики блоков/работ/материалов/доп.смен;
 * - первые ≤10 issues с указанием строки Excel через `GoToRowLink` (US4 T042).
 */
function CompletionActValidationCard({
	validation,
}: {
	validation: NonNullable<ParsingResult["completionActValidation"]>;
}) {
	const [expanded, setExpanded] = useState(validation.issuesCount > 0);

	const allOk = validation.arithmeticOk && validation.subtotalsOk && validation.grandTotalOk;
	const borderColor = allOk
		? "border-green-200 bg-green-50"
		: validation.fileStatus === "parsed_with_warnings"
			? "border-yellow-200 bg-yellow-50"
			: "border-red-200 bg-red-50";

	return (
		<div className={`mt-2 rounded-md border ${borderColor} p-3`}>
			<div className="flex items-center gap-2 text-sm">
				<BookOpen className="h-4 w-4 text-slate-600" />
				<span className="font-medium text-slate-900">Акт</span>
				<span className="text-slate-700">
					{validation.blocksCount} блоков · {validation.worksCount} работ ·{" "}
					{validation.materialsCount} материалов
					{validation.extraLaborCount > 0 && ` · ${validation.extraLaborCount} доп.смен`}
				</span>
			</div>

			<div className="mt-2 flex flex-wrap gap-3 text-xs">
				<ValidationFlag label="Арифметика" ok={validation.arithmeticOk} />
				<ValidationFlag label="Подытоги" ok={validation.subtotalsOk} />
				<ValidationFlag label="Общий итог" ok={validation.grandTotalOk} />
				{validation.issuesCount > 0 && (
					<span className="text-red-700">Ошибок: {validation.issuesCount}</span>
				)}
				{validation.warningsCount > 0 && (
					<span className="text-yellow-700">Предупреждений: {validation.warningsCount}</span>
				)}
			</div>

			{validation.issuesPreview.length > 0 && (
				<div className="mt-2">
					<button
						type="button"
						onClick={() => setExpanded(!expanded)}
						className="flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900"
					>
						{expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
						{expanded
							? "Скрыть первые расхождения"
							: `Показать первые ${validation.issuesPreview.length} расхождений${
									validation.issuesCount > validation.issuesPreview.length
										? ` из ${validation.issuesCount}`
										: ""
								}`}
					</button>

					{expanded && (
						<ul className="mt-1 space-y-1 text-xs text-slate-700">
							{validation.issuesPreview.map((issue, idx) => (
								// N3: составной key для стабильности и уникальности. Список immutable
								// (пересоздаётся при новом парсинге); idx обеспечивает уникальность для
								// issue одной строки с разным kind (напр. subtotal_mismatch_salary +
								// _materials оба указывают на subtotal._row).
								<li
									// biome-ignore lint/suspicious/noArrayIndexKey: immutable list, см. комментарий выше
									key={`${issue.kind}-${issue.row ?? "none"}-${idx}`}
									className="flex items-start gap-1.5"
								>
									<AlertTriangle className="h-3 w-3 flex-shrink-0 text-red-500 mt-0.5" />
									<span className="flex-1">
										{issue.message}
										{issue.row !== undefined && (
											<span className="ml-1">
												<GoToRowLink
													excelRow={issue.row}
													continuationRows={issue.continuationRows}
												/>
											</span>
										)}
									</span>
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</div>
	);
}

function ValidationFlag({ label, ok }: { label: string; ok: boolean }) {
	return (
		<span className="inline-flex items-center gap-1">
			{ok ? (
				<CheckCircle className="h-3.5 w-3.5 text-green-600" />
			) : (
				<XCircle className="h-3.5 w-3.5 text-red-600" />
			)}
			<span className={ok ? "text-green-800" : "text-red-800"}>{label}</span>
		</span>
	);
}

/**
 * Компонент прогресса загрузки пакета файлов.
 * Отображает список файлов с иконками статуса и счётчик прогресса.
 */
export function UploadProgress() {
	const { fileStatuses, totalFiles, completedFiles, isUploading, parsingResults, reset } =
		useUploadStore();

	if (fileStatuses.length === 0) return null;

	const hasIssues = fileStatuses.some((f) => f.status === "error" || f.status === "skipped");

	return (
		<div className="rounded-lg border border-slate-200 bg-white p-4">
			<div className="mb-3 flex items-center justify-between">
				<h3 className="text-sm font-medium text-slate-900">
					{isUploading ? "Загрузка файлов" : "Результат загрузки"}
				</h3>
				<div className="flex items-center gap-2">
					<span className="text-sm text-slate-500">
						{completedFiles} из {totalFiles}
					</span>
					{!isUploading && hasIssues && (
						<button
							type="button"
							onClick={reset}
							className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
							title="Закрыть"
						>
							<X className="h-4 w-4" />
						</button>
					)}
				</div>
			</div>

			{/* Progress bar */}
			<div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
				<div
					className="h-full rounded-full bg-blue-500 transition-all duration-300"
					style={{
						width: totalFiles > 0 ? `${(completedFiles / totalFiles) * 100}%` : "0%",
					}}
				/>
			</div>

			{/* File list */}
			<ul className="space-y-1.5">
				{fileStatuses.map((file, idx) => {
					const config = statusConfig[file.status];
					const parsingResult = parsingResults.find((r) => r.fileIndex === idx);
					const hasError = (file.status === "error" || file.status === "skipped") && file.error;
					const sheets = file.sheetStatuses;
					const showSheetList = Array.isArray(sheets) && sheets.length > 0;

					return (
						<li key={file.filename}>
							<div className="flex items-center gap-2 text-sm">
								<span className={config.color}>{config.icon}</span>
								<span className="flex-1 truncate text-slate-700">
									{file.filename}
									{!showSheetList && file.sheetName && (
										<span className="ml-1 text-slate-400">({file.sheetName})</span>
									)}
								</span>
								{!showSheetList && file.status === "done" && file.fileType && (
									<span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
										{file.fileType}
									</span>
								)}
							</div>
							{hasError && !showSheetList && (
								<div className="mt-1 ml-6 rounded bg-red-50 px-2 py-1 text-xs text-red-700">
									{file.error}
								</div>
							)}
							{showSheetList && (
								<ul className="mt-1 ml-6 space-y-0.5">
									{sheets.map((s) => {
										const sc = statusConfig[s.status];
										return (
											<li key={`${s.sheetIndex}-${s.sheetName}`}>
												<div className="flex items-center gap-2 text-xs">
													<span className={sc.color}>{sc.icon}</span>
													<span className="flex-1 truncate text-slate-600">{s.sheetName}</span>
													{s.status === "done" && s.fileType && (
														<span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
															{s.fileType}
														</span>
													)}
												</div>
												{s.status === "error" && s.error && (
													<div className="mt-0.5 ml-5 rounded bg-red-50 px-2 py-0.5 text-xs text-red-700">
														{s.error}
													</div>
												)}
											</li>
										);
									})}
								</ul>
							)}
							{parsingResult && <ParsingResultCard result={parsingResult} />}
						</li>
					);
				})}
			</ul>
		</div>
	);
}
